import * as THREE from 'three';
import { getStarSprite } from './textures';

export interface SkyBody {
  readonly direction: THREE.Vector3;
  readonly color: number;
  readonly size: number;
  readonly glow?: number;
  readonly isSun?: boolean;
}

export interface PlanetarySkyOptions {
  readonly zenithColor: number;
  readonly horizonColor: number;
  readonly atmosphereTint: number;
  readonly radius?: number;
  readonly starCount?: number;
  readonly sun?: SkyBody;
  readonly bodies?: readonly SkyBody[];
}

export class PlanetarySky {
  readonly object3D: THREE.Group;
  private readonly radius: number;

  constructor(options: PlanetarySkyOptions) {
    this.object3D = new THREE.Group();
    this.object3D.name = 'planetary-sky';
    this.radius = options.radius ?? 5000;

    this.object3D.add(this.buildDome(options));
    this.object3D.add(this.buildStarfield(options.starCount ?? 1600));
    if (options.sun) this.object3D.add(this.buildSun(options.sun));
    if (options.bodies) {
      for (const body of options.bodies) {
        this.object3D.add(this.buildBody(body));
      }
    }
  }

  private buildDome(options: PlanetarySkyOptions): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(this.radius, 40, 24);
    const material = new THREE.ShaderMaterial({
      uniforms: {
        zenithColor: { value: new THREE.Color(options.zenithColor) },
        horizonColor: { value: new THREE.Color(options.horizonColor) },
        atmosphereTint: { value: new THREE.Color(options.atmosphereTint) }
      },
      vertexShader: `
        varying vec3 vDir;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vDir = normalize(position);
          gl_Position = projectionMatrix * viewMatrix * worldPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 zenithColor;
        uniform vec3 horizonColor;
        uniform vec3 atmosphereTint;
        varying vec3 vDir;
        void main() {
          float h = clamp(vDir.y, -0.2, 1.0);
          float t = smoothstep(-0.05, 0.55, h);
          vec3 skyTop = mix(horizonColor, zenithColor, t);
          float haze = smoothstep(0.35, -0.05, h);
          vec3 hazy = mix(skyTop, atmosphereTint, haze * 0.55);
          gl_FragColor = vec4(hazy, 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'sky-dome';
    mesh.frustumCulled = false;
    mesh.renderOrder = -100;
    return mesh;
  }

  private buildStarfield(count: number): THREE.Points {
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const r = this.radius * 0.97;

    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 0.95);
      const base = i * 3;
      positions[base] = r * Math.sin(phi) * Math.cos(theta);
      positions[base + 1] = r * Math.cos(phi);
      positions[base + 2] = r * Math.sin(phi) * Math.sin(theta);

      const intensity = 0.45 + Math.random() * 0.55;
      const warm = Math.random() < 0.3;
      colors[base] = warm ? intensity : intensity * 0.9;
      colors[base + 1] = intensity * 0.95;
      colors[base + 2] = warm ? intensity * 0.82 : intensity;

      sizes[i] = 6 + Math.random() * 14;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

    const material = new THREE.PointsMaterial({
      map: getStarSprite(),
      size: 10,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      fog: false,
      blending: THREE.AdditiveBlending
    });

    const points = new THREE.Points(geometry, material);
    points.name = 'sky-stars';
    points.frustumCulled = false;
    points.renderOrder = -90;
    return points;
  }

  private buildSun(sun: SkyBody): THREE.Group {
    const group = new THREE.Group();
    group.name = 'sky-sun';
    const direction = sun.direction.clone().normalize();
    const distance = this.radius * 0.85;
    const center = direction.clone().multiplyScalar(distance);

    const coreMat = new THREE.MeshBasicMaterial({
      color: sun.color,
      transparent: true,
      opacity: 1,
      fog: false,
      toneMapped: false,
      depthWrite: false
    });
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(sun.size, 32, 20),
      coreMat
    );
    core.position.copy(center);
    core.renderOrder = -80;

    const glowColor = sun.glow ?? sun.color;
    const innerGlow = new THREE.Mesh(
      new THREE.SphereGeometry(sun.size * 1.6, 32, 20),
      new THREE.MeshBasicMaterial({
        color: glowColor,
        transparent: true,
        opacity: 0.45,
        side: THREE.BackSide,
        fog: false,
        toneMapped: false,
        depthWrite: false
      })
    );
    innerGlow.position.copy(center);
    innerGlow.renderOrder = -82;

    const outerGlow = new THREE.Mesh(
      new THREE.SphereGeometry(sun.size * 2.8, 32, 20),
      new THREE.MeshBasicMaterial({
        color: glowColor,
        transparent: true,
        opacity: 0.18,
        side: THREE.BackSide,
        fog: false,
        toneMapped: false,
        depthWrite: false
      })
    );
    outerGlow.position.copy(center);
    outerGlow.renderOrder = -84;

    group.add(outerGlow, innerGlow, core);
    return group;
  }

  private buildBody(body: SkyBody): THREE.Group {
    const group = new THREE.Group();
    group.name = 'sky-body';
    const direction = body.direction.clone().normalize();
    const distance = this.radius * 0.88;
    const center = direction.clone().multiplyScalar(distance);

    const coreMat = new THREE.MeshBasicMaterial({
      color: body.color,
      transparent: true,
      opacity: 0.95,
      fog: false,
      toneMapped: false,
      depthWrite: false
    });
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(body.size, 20, 14),
      coreMat
    );
    core.position.copy(center);
    core.renderOrder = -78;

    const haloColor = body.glow ?? body.color;
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(body.size * 1.5, 20, 14),
      new THREE.MeshBasicMaterial({
        color: haloColor,
        transparent: true,
        opacity: 0.28,
        side: THREE.BackSide,
        fog: false,
        toneMapped: false,
        depthWrite: false
      })
    );
    halo.position.copy(center);
    halo.renderOrder = -79;

    group.add(halo, core);
    return group;
  }
}
